<script setup>
// Per-inbox assignment extras (REQ-20260812-09 + feature #6 sticky):
//   - Assign agent on intake (early_assign_enabled)
//   - Sticky assignment for returning customers
// Mounted under the OSS auto-assignment toggle. Own admin API; does not touch
// the OSS inbox update flow.
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const DEBUG_LOG = false;
const log = (...args) => {
  if (DEBUG_LOG) {
    // eslint-disable-next-line no-console
    console.info('[InboxAssignmentSettings]', ...args);
  }
};

const props = defineProps({
  inboxId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.state.route?.params?.accountId);

const loading = ref(true);
const saving = ref(false);
const errorMsg = ref('');
const savedMsg = ref('');

const earlyAssignEnabled = ref(true);
const enabled = ref(true);
const alwaysToOwner = ref(true);

const endpoint = () =>
  `/api/v1/accounts/${accountId.value}/inbox_assignment_settings/${props.inboxId}`;

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await window.axios.get(endpoint());
    earlyAssignEnabled.value =
      data.early_assign_enabled == null ? true : !!data.early_assign_enabled;
    enabled.value = data.sticky_enabled == null ? true : !!data.sticky_enabled;
    alwaysToOwner.value =
      data.always_to_owner == null ? true : !!data.always_to_owner;
    log('loaded', data);
  } catch (err) {
    errorMsg.value = t('CUSTOM.COMMON.LOAD_FAILED');
    log('load failed', err);
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  errorMsg.value = '';
  savedMsg.value = '';
  const payload = {
    early_assign_enabled: earlyAssignEnabled.value,
    sticky_enabled: enabled.value,
    always_to_owner: alwaysToOwner.value,
  };
  log('save click', payload);
  try {
    const { data } = await window.axios.put(endpoint(), payload);
    earlyAssignEnabled.value =
      data.early_assign_enabled == null ? true : !!data.early_assign_enabled;
    enabled.value = data.sticky_enabled == null ? true : !!data.sticky_enabled;
    alwaysToOwner.value =
      data.always_to_owner == null ? true : !!data.always_to_owner;
    savedMsg.value = t('CUSTOM.COMMON.SAVED');
    log('saved', data);
  } catch (err) {
    errorMsg.value =
      err?.response?.data?.errors?.join(', ') || t('CUSTOM.COMMON.SAVE_FAILED');
    log('save failed', err);
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="sas-card">
    <label class="sas-switch">
      <input v-model="earlyAssignEnabled" type="checkbox" :disabled="loading" />
      <span class="sas-switch-label">
        <strong>{{ t('CUSTOM.ASSIGNMENT.EARLY_ASSIGN_TITLE') }}</strong>
        <em v-if="!earlyAssignEnabled">{{
          t('CUSTOM.ASSIGNMENT.EARLY_ASSIGN_OFF_HINT')
        }}</em>
        <em v-else>{{ t('CUSTOM.ASSIGNMENT.EARLY_ASSIGN_ON_HINT') }}</em>
      </span>
    </label>

    <label class="sas-switch sas-switch-spaced">
      <input v-model="enabled" type="checkbox" :disabled="loading" />
      <span class="sas-switch-label">
        <strong>{{ t('CUSTOM.ASSIGNMENT.STICKY_TITLE') }}</strong>
        <em v-if="!enabled">{{ t('CUSTOM.ASSIGNMENT.STICKY_OFF_HINT') }}</em>
        <em v-else>
          {{ t('CUSTOM.ASSIGNMENT.STICKY_ON_HINT') }}
        </em>
      </span>
    </label>

    <div v-if="enabled" class="sas-sub">
      <label class="sas-switch">
        <input v-model="alwaysToOwner" type="checkbox" :disabled="loading" />
        <span class="sas-switch-label">
          <strong>{{ t('CUSTOM.ASSIGNMENT.ALWAYS_OWNER') }}</strong>
          <em v-if="alwaysToOwner">
            {{ t('CUSTOM.ASSIGNMENT.ALWAYS_OWNER_ON') }}
          </em>
          <em v-else>
            {{ t('CUSTOM.ASSIGNMENT.ALWAYS_OWNER_OFF') }}
          </em>
        </span>
      </label>
      <p class="sas-note">
        {{ t('CUSTOM.ASSIGNMENT.STICKY_NOTE') }}
      </p>
    </div>

    <div class="sas-actions">
      <span v-if="savedMsg" class="sas-ok">{{ savedMsg }}</span>
      <span v-if="errorMsg" class="sas-err">{{ errorMsg }}</span>
      <button
        type="button"
        class="sas-btn"
        :disabled="loading || saving"
        @click="save"
      >
        {{
          saving
            ? t('CUSTOM.COMMON.SAVING')
            : t('CUSTOM.ASSIGNMENT.SAVE_ASSIGNMENT')
        }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.sas-card {
  border: 1px solid rgb(var(--slate-5));
  border-radius: 10px;
  padding: 16px;
  margin-top: 16px;
}
.sas-switch {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}
.sas-switch-spaced {
  margin-top: 14px;
}
.sas-switch input {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: rgb(var(--blue-9));
}
.sas-switch-label {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.sas-switch-label strong {
  font-size: 14px;
  color: rgb(var(--slate-12));
}
.sas-switch-label em {
  font-size: 12px;
  line-height: 1.6;
  color: rgb(var(--slate-11));
  font-style: normal;
}
.sas-sub {
  margin-top: 14px;
  padding: 12px 14px;
  border-left: 2px solid rgb(var(--slate-6));
  margin-left: 4px;
}
.sas-note {
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.6;
  color: rgb(var(--slate-10));
}
.sas-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}
.sas-btn {
  background: rgb(var(--blue-9));
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.sas-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.sas-ok {
  color: rgb(var(--grass-11, 22 163 74));
  font-size: 13px;
}
.sas-err {
  color: rgb(var(--ruby-11, 220 38 38));
  font-size: 13px;
}
</style>
